/**
 * My base class for adding accessibility features to WordPress classic navigation menus.
 * Provides ARIA attributes and event handling to make menus keyboard and screen-reader accessible.
 */
export class NavigationMenu {
    /**
     * Constructor to initialize the navigation menu.
     * @param {string} menuSelector - The CSS selector for the menu.
     * @param {boolean} [applyParentClickEvents=true] - If true, clicking parent menu items opens sub-menus; otherwise, disables the default link behavior.
     */
    constructor(menuSelector, applyParentClickEvents = true) {
        this.menu = document.querySelector(menuSelector);
        this.applyParentClickEvents = applyParentClickEvents;

        if (this.menu) {
            this.init();
        }
    }

	/**
     * Initializes the menu by setting ARIA attributes and adding event listeners.
     * Handles focus and click events to manage sub-menus.
     */
    init() {
        const allLinks = this.menu.querySelectorAll('a');
        const allParentLis = this.menu.querySelectorAll('.menu-item-has-children');

        allParentLis.forEach(thisLi => {
			// give each parent <li> aria-expanded
            thisLi.setAttribute('aria-expanded', 'false');
			// give each parent <a> aria-haspopup
            thisLi.querySelectorAll(':scope > a').forEach(thisA => thisA.setAttribute('aria-haspopup', 'true'));
        });

        allLinks.forEach(thisA => {
			// give each parent <li> aria-expanded
            thisA.addEventListener('focus', (e) => this.toggleLiFocus(e), true);
			// give each parent <a> aria-haspopup
            thisA.addEventListener('blur', (e) => this.toggleLiFocus(e), true);
        });

        // Allow parent links to function normally
        const parentLinks = this.menu.querySelectorAll('.menu-item-has-children > a[href="#"]');

        parentLinks.forEach(thisA => {
            thisA.addEventListener('keydown', (e) => this.openSubMenu(e), true);
            thisA.addEventListener('click', (e) => this.openSubMenu(e), true);
        });
    }

	/**
     * Toggles the focus state of a menu item.
     * Used to manage the ARIA and visual state of the item when it gains or loses focus.
     * @param {Event} event - The focus or blur event.
     */
    toggleLiFocus(event) {
        let thisLi = event.target.parentNode;
        thisLi.classList.toggle('focus');

		// if <a> is losing focus
        if (event.type === 'blur') {
			// wait to give other links a chance to catch focus from tabbing
            setTimeout(() => {
                this.handleBlurFocus(thisLi);
            }, 100);
        }
    }

	/**
     * Handles the logic for when a menu item loses focus.
     * Closes the sub-menu if no child items are focused.
     * @param {HTMLElement} thisLi - The parent menu item <li> element.
     */
    handleBlurFocus(thisLi) {
        
    }

	/**
     * Opens or closes a sub-menu based on the current state of the menu item.
     * Triggered by click or keydown (spacebar) events.
     * @param {Event} event - The click or keydown event.
     */
    openSubMenu(event) {
		// if keydown was NOT the spacebar - do nothing
        if (event.type === 'keydown' && event.which !== 32) return;

        event.preventDefault();
        let thisLi = event.target.closest('.menu-item-has-children');
        if (thisLi) {
			// set the appropriate aria-expanded on the <li>
            let newAriaExpanded = thisLi.getAttribute('aria-expanded') === 'false' ? 'true' : 'false';
            thisLi.setAttribute('aria-expanded', newAriaExpanded);
        }
    }
}



/**
 * Class for managing desktop navigation menus.
 * Adds mouseenter (hover) opening for sub-menus and disables mouse click opening/closing.
 * Extends the NavigationMenu class.
 */
export class DesktopNavigationMenu extends NavigationMenu {
	/**
     * Constructor to initialize the desktop navigation menu.
     * @param {string} menuSelector - The CSS selector for the menu.
     */
    constructor(menuSelector) {
        super(menuSelector, true); // Pass 'false' to prevent parent links opening the sub-menus when clicked
        this.headerNav = document.querySelector('.header-menu');
        this.headerNavMenu();
    }

    /**
     * Initializes the desktop menu and prevents parent links from getting focus on click.
     */
    init() {
        super.init(); // Call parent init

        const parentLinks = this.menu.querySelectorAll('.menu-item-has-children > a[href="#"]');

        parentLinks.forEach(thisA => {
            thisA.addEventListener('mousedown', (e) => e.preventDefault()); // Prevent focus on mouse click
        });
    }

    /**
     * Override the openSubMenu function in parent class to prevent dropdowns opening/closing on click
     */
    openSubMenu(event) {
        // Prevent dropdowns opening/closing on click for desktop navigation
        if (event.type === 'click' && event.target.closest('a[href="#"]')) {
            event.preventDefault();
            return; // do nothing
        }

        // Otherwise, allow normal behavior from parent class
        super.openSubMenu(event);
    }

	/**
     * Adds event listeners to handle hover interactions on desktop.
     * Uses mouseenter and mouseleave events to open and close sub-menus.
     */
    headerNavMenu() {
        this.headerNav.querySelectorAll('.menu .menu-item-has-children').forEach(thisLi => {
            thisLi.addEventListener('mouseenter', (event) => this.onMenuItemMouseEnter(event, thisLi));
            thisLi.addEventListener('mouseleave', (event) => this.onMenuItemMouseLeave(event, thisLi));
        });
    }

	/**
     * Handles mouseenter event to open sub-menu on hover.
     * @param {Event} event - The mouse enter event.
     * @param {HTMLElement} thisLi - The parent menu item <li> element.
     */
    onMenuItemMouseEnter(event, thisLi) {
        this.openSubMenuDesktop(thisLi, false);
    }

	/**
     * Handles mouseleave event to close sub-menu when the mouse leaves the area.
     * @param {Event} event - The mouse leave event.
     * @param {HTMLElement} thisLi - The parent menu item <li> element.
     */
    onMenuItemMouseLeave(event, thisLi) {
        let relatedTarget = event.relatedTarget;
        if (!thisLi.contains(relatedTarget)) {
            this.closeSubMenu(thisLi);
        }
    }

	/**
     * Opens a sub-menu, with an optional delay.
     * Used to provide smoother UX when navigating with hover.
     * @param {HTMLElement} thisLi - The parent menu item <li> element.
     * @param {boolean} delay - Whether to apply a delay before opening.
     */
    openSubMenuDesktop(thisLi, delay) {
        if (delay) {
            this.timeoutId = setTimeout(() => {
                thisLi.setAttribute('aria-expanded', true);
            }, 300);
        } else {
            thisLi.setAttribute('aria-expanded', true);
        }
    }

	/**
     * Closes a sub-menu and clears any timeout if present.
     * @param {HTMLElement} thisLi - The parent menu item <li> element.
     */
    closeSubMenu(thisLi) {
        thisLi.setAttribute('aria-expanded', 'false');
        clearTimeout(this.timeoutId);
    }

    handleBlurFocus(thisLi) {
        let hasChildren = thisLi.classList.contains('menu-item-has-children');
        let isChild = thisLi.closest('.sub-menu');

        // if <li> has children AND none of the <li>'s child links have focus
        if (hasChildren && !thisLi.querySelectorAll(':scope ul .focus').length) {
            // set <li> aria-expanded to false
            thisLi.setAttribute('aria-expanded', 'false');
        }

        // if <li> is a child
        if (isChild) {
            // iterate up through parent nodes until the start of the menu (.nav-menu)
            let self = thisLi;
            while (self && self.nodeType === 1 && !self.classList.contains('nav-menu')) { // Check if self is an element and not null
                // if a parent node is a sub-menu and doesn't contain anything that has focus - close it
                if (self.classList.contains('sub-menu') && !self.querySelectorAll(':scope .focus').length) {
                    let parentLi = self.closest('.menu-item-has-children');
                    if (parentLi) {
                        parentLi.setAttribute('aria-expanded', 'false');
                    }
                }
                self = self.parentNode;
            }
        }
    }
}